import axios from "axios"
import customAxios, { getToken } from "./helpers/auth_helpers"
import { titularResponseParser } from "./helpers/titular_helpers"

export const loginUser = async values => {
  const { name, password } = values

  try {
    const response = await customAxios.post(
      `${window.REACT_AUTH_URL}/ewanauth/v1/login`,
      {
        username: name,
        password: password,
      },
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const getUser = async () => {
  if (!getToken()) return false

  try {
    const response = await axios.get(
      `${window.REACT_AUTH_URL}/ewanauth/v1/whoami`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const getRefreshToken = async () => {
  if (!getToken()) return false

  try {
    const response = await axios.post(
      `${window.REACT_AUTH_URL}/ewanauth/v1/refreshToken`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const getMenu = async () => {
  if (!getToken()) return []

  try {
    const response = await axios.get(`${window.REACT_APP_URL}/ewanapi/v1/menu`)
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const getConfig = async () => {
  try {
    const response = await axios.get(
      `${window.REACT_CONFIG_URL}/ewanfrontstyle/v1/appconfig`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

// ── Admin ──────────────────────────────────────────────────────────────────

export const getUsers = async () => {
  if (!getToken()) return false

  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/usuario`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

// BDSJEA-129: ABM de usuarios y gestion de claves.

const parsearErroresPorCampo = subErrors => {
  if (!Array.isArray(subErrors) || subErrors.length === 0) return null
  const mapa = {}
  subErrors.forEach(sub => {
    if (sub.field) mapa[sub.field] = sub.message
  })
  return Object.keys(mapa).length > 0 ? mapa : null
}

const lanzarErrorUsuario = error => {
  const apierror = error.response?.data?.apierror
  const msg = apierror?.message || error.response?.data?.message || error.message
  const err = new Error(msg)
  err.erroresPorCampo = parsearErroresPorCampo(apierror?.subErrors)
  throw err
}

export const createUser = async usuario => {
  try {
    const response = await axios.post(
      `${window.REACT_APP_URL}/ewanapi/v1/usuario`,
      usuario,
    )
    // Devuelve { usuario, claveTemporal }. La clave viene una sola vez: si se
    // pierde, hay que resetearla de nuevo.
    return response.data
  } catch (error) {
    lanzarErrorUsuario(error)
  }
}

export const updateUser = async usuario => {
  try {
    const response = await axios.put(
      `${window.REACT_APP_URL}/ewanapi/v1/usuario`,
      usuario,
    )
    return response.data
  } catch (error) {
    lanzarErrorUsuario(error)
  }
}

export const deleteUser = async id => {
  try {
    const response = await axios.delete(
      `${window.REACT_APP_URL}/ewanapi/v1/usuario/${id}`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const resetUserPassword = async id => {
  try {
    const response = await axios.post(
      `${window.REACT_APP_URL}/ewanapi/v1/usuario/${id}/reset-clave`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const unlockUser = async id => {
  try {
    const response = await axios.post(
      `${window.REACT_APP_URL}/ewanapi/v1/usuario/${id}/desbloquear`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const changeOwnPassword = async payload => {
  try {
    const response = await axios.post(
      `${window.REACT_APP_URL}/ewanapi/v1/usuario/cambiar-clave`,
      payload,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const getProfiles = async () => {
  if (!getToken()) return []

  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/perfil`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const putProfiles = async data => {
  try {
    const response = await axios.put(
      `${window.REACT_APP_URL}/ewanapi/v1/perfil`,
      data,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const getAuditoriaLogs = async () => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/auditoria/logs`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const getAuditoriaLogsByDate = async (desde, hasta) => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/auditoria/logs/${desde}/${hasta}`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

// ── Titular / Post (used by Agro/AltaProductor) ────────────────────────────

export const listByCuit = async cuit => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/titular/listbycuit/${cuit}`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const listByName = async name => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/titular/listbyname/${name}`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

// ── Monitoreo ──────────────────────────────────────────────────────────────

export const getEmpresasCartera = async () => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/onboarding/monitoreo/empresas`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const getControlesCartera = async () => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/onboarding/monitoreo/controles`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const registrarControl = async (controlId, registro) => {
  try {
    const response = await axios.post(
      `${window.REACT_APP_URL}/ewanapi/v1/onboarding/monitoreo/controles/${controlId}/registrar`,
      registro,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

// ── TablasGenerales ────────────────────────────────────────────────────────

export const getTablasGenerales = async () => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/config/tablasgenerales`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const getTablasGeneralesByNro = async (
  nro,
  incluirInactivos = false,
) => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/config/tablasgenerales/${nro}`,
      { params: { incluirInactivos } },
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const getTablaGeneralConfig = async nro => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/config/tablasgenerales/${nro}/config`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const getTablasGeneralesCatalogos = async (incluirInactivos = false) => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/config/tablasgenerales/catalogos`,
      { params: { incluirInactivos } },
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const recuperarCatalogo = async nro => {
  try {
    const response = await axios.post(
      `${window.REACT_APP_URL}/ewanapi/v1/config/tablasgenerales/${nro}/catalogo/recuperar`,
    )
    return response.data
  } catch (error) {
    throw new Error(
      error.debugMessage || error.apierror?.debugMessage || error.message,
    )
  }
}

export const getTablaGeneralConfigEdicion = async nro => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/config/tablasgenerales/${nro}/config/edicion`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const saveTablaGeneralConfig = async (nro, config) => {
  try {
    const response = await axios.post(
      `${window.REACT_APP_URL}/ewanapi/v1/config/tablasgenerales/${nro}/config`,
      config,
    )
    return response.data
  } catch (error) {
    throw new Error(
      error.debugMessage || error.apierror?.debugMessage || error.message,
    )
  }
}

export const recuperarTablaGeneral = async id => {
  try {
    const response = await axios.post(
      `${window.REACT_APP_URL}/ewanapi/v1/config/tablasgenerales/${id}/recuperar`,
    )
    return response.data
  } catch (error) {
    throw new Error(
      error.debugMessage || error.apierror?.debugMessage || error.message,
    )
  }
}

export const deleteTablasGenerales = async id => {
  try {
    const response = await axios.delete(
      `${window.REACT_APP_URL}/ewanapi/v1/config/deletetablasgenerales/${id}`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const saveTablasGenerales = async values => {
  try {
    const response = await axios.post(
      `${window.REACT_APP_URL}/ewanapi/v1/config/savetablasgenerales`,
      values,
    )
    return response.data
  } catch (error) {
    throw new Error(
      error.debugMessage || error.apierror?.debugMessage || error.message,
    )
  }
}

// ── TablasGenerales: Tabla Externa (BDSJEA-142) ─────────────────────────────

export const getEsquemasTablaExterna = async () => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/config/tabla-externa/esquemas`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const getTablasDeEsquema = async esquema => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/config/tabla-externa/esquemas/${esquema}/tablas`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const getColumnasTablaExterna = async (esquema, tabla) => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/config/tabla-externa/tablas/${esquema}/${tabla}/columnas`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const agregarColumnaActivo = async (esquema, tabla) => {
  try {
    const response = await axios.post(
      `${window.REACT_APP_URL}/ewanapi/v1/config/tabla-externa/tablas/${esquema}/${tabla}/agregar-columna-activo`,
    )
    return response.data
  } catch (error) {
    throw new Error(
      error.debugMessage || error.apierror?.debugMessage || error.message,
    )
  }
}

export const getFilasTablaExterna = async nro => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/config/tablasgenerales/${nro}/tabla-externa/filas`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const saveFilaTablaExterna = async (nro, fila) => {
  try {
    const response = await axios.post(
      `${window.REACT_APP_URL}/ewanapi/v1/config/tablasgenerales/${nro}/tabla-externa/filas`,
      fila,
    )
    return response.data
  } catch (error) {
    throw new Error(
      error.debugMessage || error.apierror?.debugMessage || error.message,
    )
  }
}

export const deleteFilaTablaExterna = async (nro, id, fisico = false) => {
  try {
    const response = await axios.delete(
      `${window.REACT_APP_URL}/ewanapi/v1/config/tablasgenerales/${nro}/tabla-externa/filas/${id}`,
      { params: { fisico } },
    )
    return response.data
  } catch (error) {
    throw new Error(
      error.debugMessage || error.apierror?.debugMessage || error.message,
    )
  }
}

export const recuperarFilaTablaExterna = async (nro, id) => {
  try {
    const response = await axios.post(
      `${window.REACT_APP_URL}/ewanapi/v1/config/tablasgenerales/${nro}/tabla-externa/filas/${id}/recuperar`,
    )
    return response.data
  } catch (error) {
    throw new Error(
      error.debugMessage || error.apierror?.debugMessage || error.message,
    )
  }
}

export const deleteCatalogo = async (nro, fisico = false) => {
  try {
    const response = await axios.delete(
      `${window.REACT_APP_URL}/ewanapi/v1/config/tablasgenerales/${nro}/catalogo`,
      { params: { fisico } },
    )
    return response.data
  } catch (error) {
    throw new Error(
      error.debugMessage || error.apierror?.debugMessage || error.message,
    )
  }
}

// ── Documents ──────────────────────────────────────────────────────────────

export const getDocumentById = async url => {
  try {
    const response = await axios.get(url, { responseType: "blob" })
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

// ── OnBoarding ─────────────────────────────────────────────────────────────

export const validarCuit = async cuit => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/titular/valida/${cuit}`,
    )
    return response.data
  } catch (error) {
    throw new Error(
      error.response?.data?.error ||
        error.response?.data?.message ||
        error.message,
    )
  }
}

export const getDatosTitularAlta = async (cuit, esEvaluador) => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/titular/data/${cuit}/${esEvaluador}`,
    )
    return titularResponseParser(response)
  } catch (error) {
    throw new Error(
      error.response?.data?.error ||
        error.response?.data?.message ||
        error.message,
    )
  }
}

export const getEvaluacionDatosCliente = async cuit => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/evaluador/buscardatoscliente/${cuit}`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const postEvaluacionNoCliente = async data => {
  const newData = {
    titular: data.titular,
    acuerdo: data.acuerdo,
    prestamo: data.prestamo,
    tarjeta: data.tarjeta,
    lineas: data.lineas,
    checklistItems: data.documentacion.map(d => {
      return { itemName: d.itemName }
    }),
  }
  const json = JSON.stringify(newData)
  const blob = new Blob([json], {
    type: "application/json",
  })

  const formData = new FormData()
  formData.append("data", blob)
  for (var x = 0; x < data.documentacion.length; x++) {
    formData.append("files", data.documentacion[x].file)
  }

  try {
    const response = await axios.post(
      `${window.REACT_APP_URL}/ewanapi/v1/solicitud/onboarding/save`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const getChecklistByCode = async code => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/config/checklistbycode/${code}`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const getEstadosItemList = async () => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/ref/estadositemchecklist`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const solicitarOfertaEvaluacion = async evaluacion => {
  try {
    const response = await axios.post(
      `${window.REACT_APP_URL}/ewanapi/v1/solicitud/solicitarofertaevaluacion`,
      evaluacion,
    )
    return response.data
  } catch (error) {
    if (error.response) {
      const errorMessage =
        error.response.data.message || "Error al solicitar oferta"
      const customError = new Error(errorMessage)
      customError.originalError = error.response.data
      customError.status = error.response.status
      throw customError
    } else if (error.request) {
      throw new Error("No se pudo conectar con el servidor backend")
    } else {
      throw new Error("Error al solicitar oferta: " + error.message)
    }
  }
}

export const postGuardarOfertaNoCliente = async (
  plan,
  titular,
  balance,
  calificacionElegida,
) => {
  try {
    if (!balance?.FECHA_BALANCE) {
      throw new Error("La fecha de balance es requerida")
    }

    if (!balance?.Q_MESES_BALANCE || balance.Q_MESES_BALANCE <= 0) {
      throw new Error("La cantidad de meses del balance debe ser mayor a 0")
    }

    const fechaBalance = new Date(balance.FECHA_BALANCE)
    const fechaActual = new Date()
    const mesesDiferencia =
      (fechaActual.getFullYear() - fechaBalance.getFullYear()) * 12 +
      (fechaActual.getMonth() - fechaBalance.getMonth())

    if (mesesDiferencia > 18) {
      throw new Error(
        "La fecha de cierre de balance no puede ser de más de 18 meses de antigüedad",
      )
    }

    const requestBody = {
      calificacionElegida: calificacionElegida,
      titular: titular,
      balance: {
        fechaBalance: balance.FECHA_BALANCE,
        qMesesBalance: balance.Q_MESES_BALANCE || 0,
        qMesesPostBalance: balance.Q_MESES_POST_BALANCE || 0,
        totalActivoCorriente: parseFloat(balance.TOTALACTIVOCORRIENTE) || 0,
        totalActivoNoCorriente:
          parseFloat(balance.TOTAL_ACTIVO_NO_CORRIENTE) || 0,
        totalActivo: parseFloat(balance.TOTALACTIVO) || 0,
        totalPasivoCorriente: parseFloat(balance.TOTALPASIVOCORRIENTE) || 0,
        totalPasivoNoCorriente:
          parseFloat(balance.TOTAL_PASIVO_NO_CORRIENTE) || 0,
        totalPasivo: parseFloat(balance.TOTALPASIVO) || 0,
        totalPatrimonioNeto: parseFloat(balance.TOTALPATRIMONIONETO) || 0,
        resultadoFinal: parseFloat(balance.RESULTADOFINAL) || 0,
        ventas: parseFloat(balance.VENTAS) || 0,
        baVentas: parseFloat(balance.BA_VENTAS) || 0,
        ventasPromedioMensual: parseFloat(balance.VENTAS_PROMEDIO_MENSUAL) || 0,
        ventasPromedioPostBalance:
          parseFloat(balance.VENTAS_PROMEDIO_POST_BALANCE) || 0,
        variacionVentasPB: parseFloat(balance.VARIACION_VENTAS_PB) || 0,
        deudaBancaria: parseFloat(balance.DEUDABANCARIA) || null,
        ventasPostBalance1: balance.ventasPostBalance1 || {},
        ventasPostBalance2: balance.ventasPostBalance2 || {},
      },
    }

    const response = await axios.post(
      `${window.REACT_APP_URL}/ewanapi/v1/evaluador/guardarofertanocliente/${plan}`,
      requestBody,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  } catch (e) {
    throw new Error(e.response?.data?.message || e.message)
  }
}

export const getDeudaNosis = async (cuit, enMillones) => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/titular/deudanosis/${cuit}/${enMillones}`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const getEvaluacionVentasLufe = async (cuit, cierre) => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/evaluador/buscarVentasLufe/${cuit}/${cierre}`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const getOfertaNoCliente = async (plan, titular, balance) => {
  try {
    if (!balance?.FECHA_BALANCE) {
      throw new Error("La fecha de balance es requerida")
    }

    if (!balance?.Q_MESES_BALANCE || balance.Q_MESES_BALANCE <= 0) {
      throw new Error("La cantidad de meses del balance debe ser mayor a 0")
    }

    const fechaBalance = new Date(balance.FECHA_BALANCE)
    const fechaActual = new Date()
    const mesesDiferencia =
      (fechaActual.getFullYear() - fechaBalance.getFullYear()) * 12 +
      (fechaActual.getMonth() - fechaBalance.getMonth())

    if (mesesDiferencia > 18) {
      throw new Error(
        "La fecha de cierre de balance no puede ser de más de 18 meses de antigüedad",
      )
    }

    const requestBody = {
      titular: titular,
      balance: {
        fechaBalance: balance.FECHA_BALANCE,
        qMesesBalance: balance.Q_MESES_BALANCE || 0,
        qMesesPostBalance: balance.Q_MESES_POST_BALANCE || 0,
        totalActivoCorriente: parseFloat(balance.TOTALACTIVOCORRIENTE) || 0,
        totalActivoNoCorriente:
          parseFloat(balance.TOTAL_ACTIVO_NO_CORRIENTE) || 0,
        totalActivo: parseFloat(balance.TOTALACTIVO) || 0,
        totalPasivoCorriente: parseFloat(balance.TOTALPASIVOCORRIENTE) || 0,
        totalPasivoNoCorriente:
          parseFloat(balance.TOTAL_PASIVO_NO_CORRIENTE) || 0,
        totalPasivo: parseFloat(balance.TOTALPASIVO) || 0,
        totalPatrimonioNeto: parseFloat(balance.TOTALPATRIMONIONETO) || 0,
        resultadoFinal: parseFloat(balance.RESULTADOFINAL) || 0,
        ventas: parseFloat(balance.VENTAS) || 0,
        baVentas: parseFloat(balance.BA_VENTAS) || 0,
        ventasPromedioMensual: parseFloat(balance.VENTAS_PROMEDIO_MENSUAL) || 0,
        ventasPromedioPostBalance:
          parseFloat(balance.VENTAS_PROMEDIO_POST_BALANCE) || 0,
        variacionVentasPB: parseFloat(balance.VARIACION_VENTAS_PB) || 0,
        deudaBancaria: parseFloat(balance.DEUDABANCARIA) || null,
        ventasPostBalance1: balance.ventasPostBalance1 || {},
        ventasPostBalance2: balance.ventasPostBalance2 || {},
      },
    }

    const response = await axios.post(
      `${window.REACT_APP_URL}/ewanapi/v1/evaluador/ofertanocliente/${plan}`,
      requestBody,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  } catch (e) {
    throw new Error(e.response?.data?.message || e.message)
  }
}

export const getSolicitudById = async id => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/solicitud/getbyid/${id}`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const deleteDocumentSolicitud = async id => {
  try {
    const response = await axios.post(
      `${window.REACT_APP_URL}/ewanapi/v1/solicitud/deletedocument/${id}`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const getProcesoDeEvaluacion = async () => {
  try {
    const response = await axios.get(
      `${window.REACT_EVAL_URL}/api/v1/procesoevaluacion/activos`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const startEvaluation = async (procesoId, tipoProceso, cuit) => {
  try {
    const response = await axios.post(
      `${window.REACT_EVAL_URL}/api/v1/evaluacion/start`,
      {
        tipoDocumento: "CUIT",
        numeroDocumento: cuit,
        proceso: tipoProceso,
        procesoEvaluacionId: procesoId,
        esSimulacion: false,
        tipo: "quick",
      },
    )
    return response.data
  } catch (error) {
    if (error.response) {
      const errorData = error.response.data
      const errorMessage = errorData.message || "Error al ejecutar evaluación"
      const customError = new Error(errorMessage)
      customError.originalError = errorData
      customError.status = errorData.status
      throw customError
    } else if (error.request) {
      throw new Error("No se pudo conectar con el servidor de evaluación")
    } else {
      throw new Error("Error al realizar la petición de evaluación: " + error)
    }
  }
}

export const checkEvaluation = async evaluationId => {
  try {
    const response = await axios.get(
      `${window.REACT_EVAL_URL}/api/v1/evaluacion/status/${evaluationId}`,
    )
    const evaluation = response.data

    if (!evaluation) {
      throw new Error("Evaluación no encontrada")
    }

    return evaluation
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error("Credenciales inválidas")
    }
    throw new Error(
      error.message ||
        error.response?.data?.message ||
        "Error al chequear el estado de la evaluacion",
    )
  }
}

export const getEvaluationResult = async evaluationId => {
  try {
    const response = await axios.get(
      `${window.REACT_EVAL_URL}/api/v1/evaluacion/${evaluationId}`,
    )
    return response.data
  } catch (error) {
    if (error.response) {
      const errorData = error.response.data
      const errorMessage =
        errorData.message || "Error al obtener resultado de evaluación"
      const customError = new Error(errorMessage)
      customError.originalError = errorData
      customError.status = errorData.status
      throw customError
    } else if (error.request) {
      throw new Error("No se pudo conectar con el servidor")
    } else {
      throw new Error("Error al obtener resultado de evaluación")
    }
  }
}

export const guardarEvaluacion = async evaluacionData => {
  try {
    const response = await axios.post(
      `${window.REACT_APP_URL}/ewanapi/v1/evaluacion`,
      evaluacionData,
    )
    return response.data
  } catch (error) {
    if (error.response) {
      const errorMessage =
        error.response.data.message || "Error al guardar la evaluación"
      const customError = new Error(errorMessage)
      customError.originalError = error.response.data
      customError.status = error.response.status
      throw customError
    } else if (error.request) {
      throw new Error("No se pudo conectar con el servidor backend")
    } else {
      throw new Error("Error al guardar la evaluación: " + error.message)
    }
  }
}

export const consultarBCRA = async cuit => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/bcra/deudas/${cuit}`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}

export const consultarAML = async (cuit, denominacion) => {
  try {
    const response = await axios.get(
      `${window.REACT_APP_URL}/ewanapi/v1/aml/consultar/${cuit}${denominacion ? `?denominacion=${encodeURIComponent(denominacion)}` : ""}`,
    )
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message)
  }
}
